import { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import PageHero from "../components/PageHero";

const RECAPTCHA_SITE_KEY = "6LcUy-EsAAAAAITCnU1Ju6sMnBdSX8qQ0f1LK-FD";
const CONTACT_ENDPOINT = "https://api.rndo.eu/contact.php";

function Contact() {
  const captchaRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organisation: "",
    subject: "",
    message: "",
  });

  const [status, setStatus] = useState({
    type: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  function handleChange(event) {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();

    setStatus({
      type: "",
      message: "",
    });

    const captchaToken = captchaRef.current?.getValue();

    if (!captchaToken) {
      setStatus({
        type: "error",
        message: "Please complete the captcha before sending your message.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          captchaToken,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || "Something went wrong.");
      }

      setFormData({
        name: "",
        email: "",
        organisation: "",
        subject: "",
        message: "",
      });

      captchaRef.current?.reset();

      setStatus({
        type: "success",
        message: "Your message has been sent successfully. Thank you for contacting R&Do.",
      });
    } catch (error) {
      captchaRef.current?.reset();

      setStatus({
        type: "error",
        message: "Your message could not be sent. Please try again or contact us directly at info@rndo.eu.",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main>
      <PageHero
        title="Contact R&Do"
        text="Have an idea, research result, prototype, partnership opportunity or digital challenge? Let’s discuss how it can move from potential to practical value."
      />

      <section className="section contact-panel enhanced-contact">
        <div className="contact-intro">
          <span className="eyebrow">Start a conversation</span>
          <h2>Let’s shape the next step together.</h2>

          <p>
            Whether you are developing a new product, managing a European project,
            exploring commercialisation opportunities or looking for a partner in
            digital solutions, communication, exploitation or IPR management, R&Do
            can help turn the discussion into a clear path forward.
          </p>

          <div className="contact-highlights">
            <article>
              <h3>Ideas & prototypes</h3>
              <p>
                Share early concepts, working products or research outcomes with
                market potential.
              </p>
            </article>

            <article>
              <h3>Projects & partnerships</h3>
              <p>
                Discuss EU projects, dissemination, exploitation, networking and
                strategic collaboration.
              </p>
            </article>

            <article>
              <h3>Digital solutions</h3>
              <p>
                Explore platforms, data tools, learning environments and AI-ready
                services.
              </p>
            </article>
          </div>
        </div>

        <form className="contact-form" onSubmit={handleSubmit}>
          <div className="form-row two">
            <label>
              Name
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Your name"
              />
            </label>

            <label>
              Email
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="you@example.com"
              />
            </label>
          </div>

          <label>
            Organisation / Company
            <input
              type="text"
              name="organisation"
              value={formData.organisation}
              onChange={handleChange}
              placeholder="Optional"
            />
          </label>

          <label>
            Subject
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              placeholder="How can R&Do help?"
            />
          </label>

          <label>
            Message
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows="7"
              placeholder="Tell us about your idea, project, product, challenge or partnership opportunity."
            />
          </label>

          <div className="captcha-wrap">
            <ReCAPTCHA ref={captchaRef} sitekey={RECAPTCHA_SITE_KEY} />
          </div>

          {status.message && (
            <div className={`form-status ${status.type}`}>
              {status.message}
            </div>
          )}

          <button className="btn primary form-submit" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Sending..." : "Send Message"}
          </button>

        </form>
      </section>
    </main>
  );
}

export default Contact;